import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createManualTransaction,
  resolveInboxItemCombined,
  ManualTransaction,
  ManualTransactionBody,
  ManualTransactionUpdateBody,
  MissingCostBasisItem,
  ZeroCostAcquisitionResolution,
  updateManualTransaction,
} from '../../api/tax';
import { useEscToClose } from '../../hooks/useEscToClose';
import InboxRowContext from './InboxRowContext';
import './ManualTransactionsView.css';

// =============================================================================
// ManualTransactionModal — create/edit form for a single manual transaction.
//
// Three call shapes:
// - mode='create', source='crud' (default): standalone CRUD create. Posts a
//   new record via createManualTransaction. No reason required.
// - mode='edit', source='crud':   PUTs to /v1/tax/manual-transactions/{id}.
//   Reason field required so the audit trail explains why the record changed.
// - mode='create', source='inbox' (TASK-018): inbox-driven resolution of a
//   `ZERO_COST_ACQUISITION` row. Renders `InboxRowContext` at the top,
//   locks portfolio/product/date/quantity (visually disabled, derived from
//   the inbox row), exposes only cost-per-unit (USD) and reason inputs, and
//   submits via `resolveInboxItemCombined({resolutionType:
//   'MANUAL_TRANSACTION_CREATED', ...})` so the manual transaction + the
//   inbox_resolution row are written in one DB transaction. On success the
//   ['tax','manual-transactions'] and ['missingCostBasis'] queries are
//   invalidated so both the parent inbox and the manual-transactions view
//   reflect the resolution without a manual page reload (REQ-014).
//
// Server errors surface as inline form text below the submit button.
// Modal styling reuses the shared modal-overlay / modal-content / modal-pill-*
// classes from ConfigModal.css and CancelTradeGroupModal.css.
// =============================================================================

export interface ManualTransactionModalProps {
  mode: 'create' | 'edit';
  /** Pre-filled record. Required for edit mode; optional in create mode. */
  initial?: ManualTransaction;
  /** Optional pre-fill overrides used by InboxResolutionFlow in create mode. */
  portfolioId?: string;
  productId?: string;
  transactionDate?: string;
  quantity?: string;
  /**
   * Inbox row context (TASK-015 / TASK-018). When `source === 'inbox'` the
   * modal renders the inbox context block, locks portfolio/product/date/
   * quantity as read-only, exposes cost+reason inputs, and submits via the
   * combined inbox-resolution endpoint.
   */
  inboxItem?: MissingCostBasisItem;
  /**
   * Drives the inbox-mode rendering & submit path (TASK-018). Defaults to
   * `'crud'` so existing call sites that open the modal from the manual
   * transactions CRUD view are unchanged.
   */
  source?: 'inbox' | 'crud';
  onClose: () => void;
  onSuccess: () => void;
}

interface FormState {
  portfolioId: string;
  productId: string;
  tradeSide: 'BUY' | 'SELL';
  transactionDate: string;
  quantity: string;
  pricePerUnit: string;
  fees: string;
  description: string;
  sourceReference: string;
  reason: string;
}

function isoToDateInput(iso: string | undefined): string {
  if (!iso) return '';
  // The transaction_date field is a YYYY-MM-DD UTC date. Strip any time portion
  // an ISO timestamp might carry so the <input type="date"> binds cleanly.
  const idx = iso.indexOf('T');
  return idx >= 0 ? iso.substring(0, idx) : iso;
}

const ManualTransactionModal: React.FC<ManualTransactionModalProps> = ({
  mode,
  initial,
  portfolioId,
  productId,
  transactionDate,
  quantity,
  inboxItem,
  source = 'crud',
  onClose,
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  const isInbox = source === 'inbox' && !!inboxItem;

  const [form, setForm] = useState<FormState>(() => ({
    portfolioId: inboxItem?.portfolio_id ?? initial?.portfolio_id ?? portfolioId ?? '',
    productId: inboxItem?.product_id ?? initial?.product_id ?? productId ?? '',
    tradeSide: initial?.trade_side ?? 'BUY',
    transactionDate: isoToDateInput(
      inboxItem?.occurrence_date ?? initial?.transaction_date ?? transactionDate,
    ),
    quantity: inboxItem?.quantity ?? initial?.quantity ?? quantity ?? '',
    pricePerUnit: initial?.price_per_unit ?? '',
    fees: initial?.fees ?? '0',
    description: initial?.description ?? '',
    sourceReference: initial?.source_reference ?? '',
    reason: '',
  }));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (isInbox) {
      if (!form.pricePerUnit.trim()) {
        next.pricePerUnit = 'Cost per unit (USD) is required';
      } else if (!(parseFloat(form.pricePerUnit) > 0)) {
        next.pricePerUnit = 'Cost must be positive';
      }
      if (!form.reason.trim()) next.reason = 'Reason is required';
      setErrors(next);
      return Object.keys(next).length === 0;
    }

    if (!form.portfolioId.trim()) next.portfolioId = 'Portfolio is required';
    if (!form.productId.trim()) next.productId = 'Product is required';
    if (!form.transactionDate.trim()) next.transactionDate = 'Date is required';
    if (!form.quantity.trim()) {
      next.quantity = 'Quantity is required';
    } else if (!(parseFloat(form.quantity) > 0)) {
      next.quantity = 'Quantity must be positive';
    }
    if (!form.pricePerUnit.trim()) {
      next.pricePerUnit = 'Price is required';
    } else if (!(parseFloat(form.pricePerUnit) > 0)) {
      next.pricePerUnit = 'Price must be positive';
    }
    if (!form.fees.trim()) {
      next.fees = 'Fees are required (use 0 for none)';
    } else if (parseFloat(form.fees) < 0 || Number.isNaN(parseFloat(form.fees))) {
      next.fees = 'Fees must be 0 or positive';
    }
    if (!form.description.trim()) next.description = 'Description is required';
    if (mode === 'edit' && !form.reason.trim()) {
      next.reason = 'Reason is required for edits';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const createMutation = useMutation({
    mutationFn: (body: ManualTransactionBody) => createManualTransaction(body),
    onSuccess: () => onSuccess(),
    onError: (err: unknown) => {
      setServerError(err instanceof Error ? err.message : 'Failed to create transaction');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: number; body: ManualTransactionUpdateBody }) =>
      updateManualTransaction(id, body),
    onSuccess: () => onSuccess(),
    onError: (err: unknown) => {
      setServerError(err instanceof Error ? err.message : 'Failed to update transaction');
    },
  });

  // Inbox-mode submit: call the combined endpoint with
  // resolutionType=MANUAL_TRANSACTION_CREATED. Invalidates both the manual-
  // transactions list and the missingCostBasis query so the inbox refreshes
  // on success (REQ-014).
  const inboxResolveMutation = useMutation({
    mutationFn: (body: ZeroCostAcquisitionResolution) => resolveInboxItemCombined(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tax', 'manual-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['missingCostBasis'] });
      onSuccess();
    },
    onError: (err: unknown) => {
      setServerError(err instanceof Error ? err.message : 'Failed to resolve inbox item');
    },
  });

  const isSaving =
    createMutation.isPending || updateMutation.isPending || inboxResolveMutation.isPending;

  // In inbox mode the submit button stays disabled until cost-per-unit + reason
  // are both filled (AC-U-008). Other inputs are locked & derived from the
  // inbox row, so they are always populated.
  const isInboxSubmitDisabled =
    isInbox && (!form.pricePerUnit.trim() || !form.reason.trim());

  useEscToClose(onClose, { disabled: isSaving });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setServerError(null);
    if (!validate()) return;

    if (isInbox && inboxItem) {
      // For ZERO_COST_ACQUISITION the source identifier is the Coinbase
      // acquisition order ID (when surfaced by REQ-012) and falls back to
      // source_order_id otherwise. Matches the chooser's derivation in
      // InboxResolutionFlow.
      const sourceIdentifier =
        inboxItem.acquisition_order_id ?? inboxItem.source_order_id;
      // For ZERO_COST_ACQUISITION the productId, transactionDate, and quantity
      // are inherited read-only from the inbox row (the originating acquisition);
      // the operator supplies only the missing per-unit cost.
      inboxResolveMutation.mutate({
        portfolioId: inboxItem.portfolio_id,
        sourceIdentifier,
        itemType: 'ZERO_COST_ACQUISITION',
        resolutionType: 'MANUAL_TRANSACTION_CREATED',
        productId: inboxItem.product_id,
        transactionDate: inboxItem.occurrence_date,
        quantity: inboxItem.quantity,
        pricePerUnit: form.pricePerUnit.trim(),
        reason: form.reason.trim(),
        resolvedQuantity: inboxItem.quantity,
      });
      return;
    }

    if (mode === 'create') {
      const body: ManualTransactionBody = {
        portfolioId: form.portfolioId.trim(),
        productId: form.productId.trim(),
        tradeSide: form.tradeSide,
        transactionDate: form.transactionDate,
        quantity: form.quantity,
        pricePerUnit: form.pricePerUnit,
        fees: form.fees,
        description: form.description.trim(),
        ...(form.sourceReference.trim()
          ? { sourceReference: form.sourceReference.trim() }
          : {}),
      };
      createMutation.mutate(body);
    } else {
      if (!initial) {
        setServerError('Cannot edit: missing existing record');
        return;
      }
      const body: ManualTransactionUpdateBody = {
        portfolioId: form.portfolioId.trim(),
        productId: form.productId.trim(),
        tradeSide: form.tradeSide,
        transactionDate: form.transactionDate,
        quantity: form.quantity,
        pricePerUnit: form.pricePerUnit,
        fees: form.fees,
        description: form.description.trim(),
        ...(form.sourceReference.trim()
          ? { sourceReference: form.sourceReference.trim() }
          : {}),
        reason: form.reason.trim(),
      };
      updateMutation.mutate({ id: initial.transaction_id, body });
    }
  };

  const headerTitle = isInbox
    ? 'Resolve Zero-Cost Acquisition'
    : mode === 'create'
      ? 'New Manual Transaction'
      : 'Edit Manual Transaction';

  return (
    <div className="modal-overlay" onClick={isSaving ? undefined : onClose}>
      <div
        className="modal-content manual-tx-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>{headerTitle}</h2>
          <button
            type="button"
            className="close-button"
            onClick={onClose}
            disabled={isSaving}
            aria-label="Close manual transaction form"
          >
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {isInbox && inboxItem && <InboxRowContext item={inboxItem} />}
            <div className="manual-tx-form-grid">
              <div className="form-group">
                <label htmlFor="mt-portfolio">
                  Portfolio <span className="required">*</span>
                </label>
                <input
                  id="mt-portfolio"
                  type="text"
                  className="form-input"
                  value={form.portfolioId}
                  onChange={(e) => update('portfolioId', e.target.value)}
                  disabled={isSaving || isInbox}
                  readOnly={isInbox}
                />
                {errors.portfolioId && (
                  <div className="form-error">{errors.portfolioId}</div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="mt-product">
                  Product <span className="required">*</span>
                </label>
                <input
                  id="mt-product"
                  type="text"
                  className="form-input"
                  value={form.productId}
                  onChange={(e) => update('productId', e.target.value)}
                  placeholder="BTC-USDC"
                  disabled={isSaving || isInbox}
                  readOnly={isInbox}
                />
                {errors.productId && (
                  <div className="form-error">{errors.productId}</div>
                )}
              </div>

              {!isInbox && (
                <div className="form-group">
                  <label htmlFor="mt-side">
                    Side <span className="required">*</span>
                  </label>
                  <select
                    id="mt-side"
                    className="form-select"
                    value={form.tradeSide}
                    onChange={(e) =>
                      update('tradeSide', e.target.value as 'BUY' | 'SELL')
                    }
                    disabled={isSaving}
                  >
                    <option value="BUY">BUY</option>
                    <option value="SELL">SELL</option>
                  </select>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="mt-date">
                  Date <span className="required">*</span>
                </label>
                <input
                  id="mt-date"
                  type="date"
                  className="form-input"
                  value={form.transactionDate}
                  onChange={(e) => update('transactionDate', e.target.value)}
                  disabled={isSaving || isInbox}
                  readOnly={isInbox}
                />
                {errors.transactionDate && (
                  <div className="form-error">{errors.transactionDate}</div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="mt-quantity">
                  Quantity <span className="required">*</span>
                </label>
                <input
                  id="mt-quantity"
                  type="number"
                  step="any"
                  min="0"
                  className="form-input"
                  value={form.quantity}
                  onChange={(e) => update('quantity', e.target.value)}
                  disabled={isSaving || isInbox}
                  readOnly={isInbox}
                />
                {errors.quantity && <div className="form-error">{errors.quantity}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="mt-price">
                  {isInbox ? 'Cost per unit (USD)' : 'Price per unit (USD)'}{' '}
                  <span className="required">*</span>
                </label>
                <input
                  id="mt-price"
                  type="number"
                  step="any"
                  min="0"
                  className="form-input"
                  value={form.pricePerUnit}
                  onChange={(e) => update('pricePerUnit', e.target.value)}
                  disabled={isSaving}
                />
                {errors.pricePerUnit && (
                  <div className="form-error">{errors.pricePerUnit}</div>
                )}
              </div>

              {!isInbox && (
                <div className="form-group">
                  <label htmlFor="mt-fees">
                    Fees (USD) <span className="required">*</span>
                  </label>
                  <input
                    id="mt-fees"
                    type="number"
                    step="any"
                    min="0"
                    className="form-input"
                    value={form.fees}
                    onChange={(e) => update('fees', e.target.value)}
                    disabled={isSaving}
                  />
                  {errors.fees && <div className="form-error">{errors.fees}</div>}
                </div>
              )}

              {!isInbox && (
                <div className="form-group manual-tx-form-grid__full">
                  <label htmlFor="mt-description">
                    Description <span className="required">*</span>
                  </label>
                  <input
                    id="mt-description"
                    type="text"
                    className="form-input"
                    value={form.description}
                    onChange={(e) => update('description', e.target.value)}
                    placeholder="Why this transaction is being recorded"
                    disabled={isSaving}
                  />
                  {errors.description && (
                    <div className="form-error">{errors.description}</div>
                  )}
                </div>
              )}

              {!isInbox && (
                <div className="form-group manual-tx-form-grid__full">
                  <label htmlFor="mt-source-ref">Source reference</label>
                  <input
                    id="mt-source-ref"
                    type="text"
                    className="form-input"
                    value={form.sourceReference}
                    onChange={(e) => update('sourceReference', e.target.value)}
                    placeholder="External reference (optional)"
                    disabled={isSaving}
                  />
                </div>
              )}

              {(mode === 'edit' || isInbox) && (
                <div className="form-group manual-tx-form-grid__full">
                  <label htmlFor="mt-reason">
                    {isInbox ? 'Reason' : 'Reason for edit'}{' '}
                    <span className="required">*</span>
                  </label>
                  <input
                    id="mt-reason"
                    type="text"
                    className="form-input"
                    value={form.reason}
                    onChange={(e) => update('reason', e.target.value)}
                    placeholder={
                      isInbox
                        ? 'Why is this acquisition cost being recorded?'
                        : 'Why is this record being changed?'
                    }
                    disabled={isSaving}
                  />
                  {errors.reason && <div className="form-error">{errors.reason}</div>}
                </div>
              )}
            </div>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="modal-pill-button modal-pill-cancel"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="modal-pill-button modal-pill-primary"
              disabled={isSaving || isInboxSubmitDisabled}
            >
              {isSaving
                ? 'Saving…'
                : isInbox
                  ? 'Resolve'
                  : mode === 'create'
                    ? 'Create'
                    : 'Save changes'}
            </button>
          </div>
          {serverError && (
            <div className="manual-tx-server-error" role="alert">
              {serverError}
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default ManualTransactionModal;
